import MobileBottomSheet from './MobileBottomSheet';
import MobileOptionList, { OptionItem } from './MobileOptionList';
import { SxProps, Theme, TextField, Box } from '@mui/material';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import MobileLoadingOverlay from 'app/pages/mobile/components/MobileLoadingOverlay';
import debounce from 'lodash/debounce';
import { sentryMaskIf } from 'sentryReplayPrivacy';

interface MobileOptionMenuProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  options: OptionItem[];
  onSelect?: (option: OptionItem) => void;
  sx?: SxProps<Theme>;
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearch?: (term: string) => void;
  onClearSearch?: () => void;
  searchDebounceMs?: number;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  isLoading?: boolean;
  /** Mask the search text and option names in session replays (person-valued options). */
  maskOptionNames?: boolean;
}

const MobileOptionMenu = ({
  open,
  onClose,
  title,
  options,
  onSelect,
  sx,
  searchable = false,
  searchPlaceholder = 'Search...',
  onSearch,
  onClearSearch,
  searchDebounceMs = 300,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  isLoading = false,
  maskOptionNames = false,
}: MobileOptionMenuProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const prevSearchTermRef = useRef('');

  // Debounced search callback - matches webapp TableDropdown behavior
  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        if (onSearch && term) {
          onSearch(term);
        }
      }, searchDebounceMs),
    [onSearch, searchDebounceMs]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Handle search input change - matches webapp TableDropdown behavior
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = event.target.value;
      setSearchTerm(newSearchTerm);

      if (newSearchTerm) {
        // User is typing - debounce the search
        debouncedSearch(newSearchTerm);
      } else if (prevSearchTermRef.current) {
        // User cleared the search (had something before, now empty)
        // Cancel any pending debounced search and clear search state
        debouncedSearch.cancel();
        onClearSearch?.();
      }

      prevSearchTermRef.current = newSearchTerm;
    },
    [debouncedSearch, onClearSearch]
  );

  // Reset search term and clear search state when menu closes
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      debouncedSearch.cancel();
      // Clear underlying search state if there was a previous search term
      if (prevSearchTermRef.current) {
        onClearSearch?.();
      }
      prevSearchTermRef.current = '';
    }
  }, [open, debouncedSearch, onClearSearch]);

  return (
    <MobileBottomSheet open={open} onClose={onClose} title={title} sx={sx}>
      <MobileLoadingOverlay isLoading={isLoading} />
      {searchable && (
        <TextField
          fullWidth
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          inputProps={sentryMaskIf(maskOptionNames)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            sx: {
              px: 2,
              py: 1,
              borderRadius: 4,
            },
          }}
          sx={{
            flexShrink: 0,
            '& input': {
              fontSize: '16px',
            },
          }}
        />
      )}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
        <MobileOptionList
          options={options}
          onSelect={onSelect}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          isLoadingMore={isLoadingMore}
          isLoading={isLoading}
          maskOptionNames={maskOptionNames}
        />
      </Box>
    </MobileBottomSheet>
  );
};

export default MobileOptionMenu;
